import { Coins, Gavel, History, Target, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import i18n from '@/i18n';
import { api, isApiError } from '../../api';
import type { BackorderDetail, BackorderEvent } from '../../api/areas/market';
import { Callout, Section } from '../../components/blocks';
import { FactRows, HeroCard, Stepper, Timeline, TwoColumn, type Step } from '../../components/flow';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { DomainLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, Loading } from '../../components/States';
import { formatCount, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { CancelBackorderButton } from '../operator/RegistryMarket';
import { RefreshButton } from '../registry/shared';
import { AUCTION_STATUS, BACKORDER_STATUS, CATCH_STATUS, checkResult, CustomerRef, DROP_STATUS, HOLD_STATUS, MarketBadge, marketEnum, money } from './shared';

const LIVE = ['ACTIVE', 'ASSIGNED', 'IN_AUCTION', 'WON', 'REGISTERING'];

/** Events that ended badly get a warning dot; the ones that won the name get a good one. */
function eventTone(type: BackorderEvent['event_type']): 'ok' | 'warn' | 'info' {
  if (type === 'BACKORDER_WON' || type === 'BACKORDER_CAUGHT') return 'ok';
  if (type === 'BACKORDER_LOST' || type === 'BACKORDER_NOT_CAUGHT' || type === 'BACKORDER_CANCELLED') return 'warn';
  return 'info';
}

/** Where this backorder is on its way (reference 61 "Sau khi bắt được tên miền"), from its own state. */
function backorderSteps(b: BackorderDetail): Step[] {
  const tm = (k: string) => (i18n.t as unknown as (key: string) => string)(`market:bo.step.${k}`);
  const s = b.status;
  const ended = ['CAUGHT', 'WON', 'NOT_CAUGHT', 'LOST', 'CANCELLED'].includes(s);
  const allocated = ['ASSIGNED', 'IN_AUCTION', 'WON', 'LOST', 'REGISTERING', 'CAUGHT', 'NOT_CAUGHT'].includes(s);
  const catching = ['REGISTERING', 'CAUGHT', 'NOT_CAUGHT'].includes(s) || b.attempt !== null;
  const bad = s === 'NOT_CAUGHT' || s === 'LOST' || s === 'CANCELLED';
  return [
    { id: 'placed', label: tm('placed'), hint: tm('placedHint'), state: 'done' },
    { id: 'drop', label: tm('drop'), hint: b.drop ? formatDateTime(b.drop.expected_drop_at) : tm('dropHint'), state: allocated || ended ? 'done' : s === 'ACTIVE' ? 'current' : 'todo' },
    {
      id: 'allocate',
      label: tm('allocate'),
      hint: s === 'IN_AUCTION' || b.auction ? tm('allocateAuction') : tm('allocateDirect'),
      state: s === 'IN_AUCTION' ? 'current' : allocated ? (s === 'LOST' ? 'blocked' : 'done') : s === 'CANCELLED' ? 'skipped' : 'todo',
    },
    { id: 'catch', label: tm('catch'), hint: tm('catchHint'), state: s === 'REGISTERING' || s === 'ASSIGNED' ? 'current' : catching && ended ? (s === 'NOT_CAUGHT' ? 'blocked' : 'done') : ended ? 'skipped' : 'todo' },
    { id: 'result', label: tm('result'), hint: ended ? marketEnum('backorderStatus', s) : tm('resultHint'), state: ended ? (bad ? 'blocked' : 'done') : 'todo' },
  ];
}

function AuctionSection({ b }: { b: BackorderDetail }) {
  const { t } = useTranslation('market');
  return (
    <Section
      title={
        <span className="flex items-center gap-2">
          <Gavel className="size-4 text-muted-foreground" aria-hidden="true" />
          {t('backorder.auction')}
        </span>
      }
    >
      {b.auction ? (
        <KeyValue
          columns={3}
          rows={[
            [t('backorders.status'), <MarketBadge key="s" group="auctionStatus" map={AUCTION_STATUS} value={b.auction.status} />],
            [t('backorder.winner'), <CustomerRef key="w" id={b.auction.winner_customer_id} name={b.auction.winner_name} />],
            [t('backorder.finalPrice'), b.auction.final_price ? money(b.auction.final_price, b.auction.currency) : null],
            [t('backorder.bids'), formatCount(b.auction.bid_count)],
            [t('auctions.ends'), formatDateTime(b.auction.ends_at)],
            [
              t('backorder.openAuction'),
              <EntityRef key="l" label={t('backorder.openAuction')} id={b.auction.id} to={`/auctions/${encodeURIComponent(b.auction.id)}`} />,
            ],
          ]}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t('backorder.noAuction')}</p>
      )}
    </Section>
  );
}

/** `/backorders/:id`: one customer's request to catch a dropping name, with its queue place, money, drop, auction, catch attempt and history. */
export function BackorderPage() {
  const { t } = useTranslation('market');
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getBackorder(id, { signal }), [id]);
  const b = result.status === 'success' ? result.data : undefined;
  useDocumentTitle(b ? t('backorder.docTitle', { domain: b.domain_name }) : t('backorder.title'));
  const crumbs = [{ label: t('backorders.title'), to: '/backorders?tab=requests' }, { label: b?.domain_name ?? '…' }];

  if (!b) {
    return (
      <>
        <PageHeader title={t('backorder.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload} title={t('backorder.loadError')} />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const attempt = b.attempt;
  const steps = backorderSteps(b);
  return (
    <>
      <PageHeader
        title={t('bo.detail.title', { domain: b.domain_name })}
        crumbs={crumbs}
        subtitle={t('bo.detail.subtitle', { customer: b.customer_name ?? '—', level: marketEnum('level', b.level) })}
        actions={
          <>
            <RefreshButton onClick={result.reload} />
            <CancelBackorderButton backorder={b} onChanged={result.reload} />
          </>
        }
      />
      <TwoColumn
        main={
          <>
            <HeroCard
              kicker={t('bo.detail.kicker')}
              title={b.domain_name}
              badge={<MarketBadge group="backorderStatus" map={BACKORDER_STATUS} value={b.status} />}
              subtitle={
                b.drop
                  ? t('bo.detail.dropLine', { status: marketEnum('dropStatus', b.drop.status), source: marketEnum('dropSource', b.drop.source) })
                  : t('backorder.noDrop')
              }
            >
              <FactRows
                rows={[
                  [t('drop.expected'), b.drop ? `${formatDateTime(b.drop.expected_drop_at)} (${formatRelative(b.drop.expected_drop_at)})` : '—'],
                  [t('drop.sourceReference'), b.drop?.source_reference ?? '—'],
                  [t('backorder.registry'), b.registry_name],
                  LIVE.includes(b.status) && [t('backorder.queue'), t('backorder.queuePosition', { position: b.queue_position, count: b.queue_size })],
                ]}
              />
            </HeroCard>
            {b.status_reason ? (
              <Callout tone="info" title={t('backorder.statusReason')}>
                {b.status_reason}
              </Callout>
            ) : null}
            <Section title={t('bo.detail.flow')} description={t('bo.detail.flowHint')}>
              <Stepper label={t('bo.detail.flow')} steps={steps} />
            </Section>
            <Section title={t('backorder.request')}>
              <KeyValue
                columns={3}
                rows={[
                  [t('backorder.customer'), <CustomerRef key="c" id={b.customer_id} name={b.customer_name} />],
                  [t('backorder.registrant'), <EntityRef key="s" label={b.subject_name} id={b.subject_id} to={`/subjects/${encodeURIComponent(b.subject_id)}`} />],
                  [t('backorder.domain'), <DomainLink key="d" keyOrName={b.domain_name} />],
                  [t('backorder.level'), marketEnum('level', b.level)],
                  [t('backorder.priority'), formatCount(b.priority_rank)],
                  [t('backorder.nameservers'), b.nameservers.length ? b.nameservers.join(', ') : null],
                  [t('backorder.agreement'), b.accepted_agreement_version],
                  [t('backorder.placed'), `${formatDateTime(b.created_at)} (${formatRelative(b.created_at)})`],
                  [t('backorder.updated'), formatDateTime(b.updated_at)],
                  [t('backorder.id'), <CopyText key="id" value={b.id} />],
                ]}
              />
            </Section>
            <div className="grid grid-cols-1 items-start gap-5 2xl:grid-cols-2 [&>*]:min-w-0">
              <Section
                title={
                  <span className="flex items-center gap-2">
                    <Timer className="size-4 text-muted-foreground" aria-hidden="true" />
                    {t('backorder.drop')}
                  </span>
                }
              >
                {b.drop ? (
                  <KeyValue
                    columns={2}
                    rows={[
                      [t('backorders.status'), <MarketBadge key="ds" group="dropStatus" map={DROP_STATUS} value={b.drop.status} />],
                      [t('drop.source'), marketEnum('dropSource', b.drop.source)],
                      [t('backorder.openDrop'), <EntityRef key="dl" label={b.drop.domain_name} id={b.drop.id} to={`/drops/${encodeURIComponent(b.drop.id)}`} />],
                    ]}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{t('backorder.noDrop')}</p>
                )}
              </Section>
              <Section
                title={
                  <span className="flex items-center gap-2">
                    <Target className="size-4 text-muted-foreground" aria-hidden="true" />
                    {t('backorder.attempt')}
                  </span>
                }
              >
                {attempt ? (
                  <KeyValue
                    columns={2}
                    rows={[
                      [t('backorders.status'), <MarketBadge key="as" group="catchStatus" map={CATCH_STATUS} value={attempt.status} />],
                      [t('backorder.window'), `${formatDateTime(attempt.window_start)} → ${formatDateTime(attempt.window_end)}`],
                      [t('backorder.checks'), <span key="ck" title={t('backorder.interval', { ms: attempt.poll_interval_ms })}>{formatCount(attempt.checks_sent)}</span>],
                      [t('backorder.lastCheck'), checkResult(attempt.last_check_result)],
                      [t('backorder.outcome'), attempt.outcome_reason],
                      [t('backorder.finished'), attempt.completed_at ? formatDateTime(attempt.completed_at) : null],
                    ]}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{t('backorder.noAttempt')}</p>
                )}
              </Section>
            </div>
            <AuctionSection b={b} />
          </>
        }
        side={
          <>
            <Section
              title={
                <span className="flex items-center gap-2">
                  <Coins className="size-4 text-muted-foreground" aria-hidden="true" />
                  {t('bo.detail.summary')}
                </span>
              }
              description={t('backorder.holdNote')}
            >
              <FactRows
                rows={[
                  [t('backorder.price'), <b key="p">{money(b.price, b.currency)}</b>],
                  [t('backorder.holdAmount'), money(b.hold.amount, b.hold.currency)],
                  [t('backorders.status'), <MarketBadge key="hs" group="holdStatus" map={HOLD_STATUS} value={b.hold.status} />],
                  [t('backorder.holdPurpose'), marketEnum('holdPurpose', b.hold.purpose)],
                  [t('backorder.holdExpires'), b.hold.expires_at ? formatDateTime(b.hold.expires_at) : null],
                  [t('backorder.holdClosed'), b.hold.closed_at ? formatDateTime(b.hold.closed_at) : null],
                  [t('bo.detail.cancel'), b.status === 'ACTIVE' ? t('bo.detail.cancelOpen') : t('bo.detail.cancelClosed')],
                ]}
              />
              <Link to={`/holds/${encodeURIComponent(b.hold.id)}`} className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
                {t('bo.detail.openHold')}
              </Link>
            </Section>
            <Callout tone="warn" title={t('bo.detail.notYetTitle')}>
              {t('bo.detail.notYet')}
            </Callout>
            <Section
              title={
                <span className="flex items-center gap-2">
                  <History className="size-4 text-muted-foreground" aria-hidden="true" />
                  {t('backorder.history')}
                </span>
              }
              description={t('backorder.historyDesc')}
            >
              <Timeline
                empty={t('backorder.noHistory')}
                items={b.events.map((e) => ({
                  id: e.id,
                  tone: eventTone(e.event_type),
                  title: <span title={e.event_type}>{marketEnum('backorderEvent', e.event_type)}</span>,
                  time: <time dateTime={e.occurred_at}>{formatDateTime(e.occurred_at)}</time>,
                  meta: e.actor_name ? <EntityRef label={e.actor_name} id={e.actor_id} /> : marketEnum('actorType', e.actor_type),
                  body: typeof e.payload.reason === 'string' ? e.payload.reason : undefined,
                }))}
              />
            </Section>
          </>
        }
      />
    </>
  );
}
